/*
 * Shilajit Global — format selector quiz.
 *
 * A tag-matching engine rather than a hard-coded decision tree. Each answer
 * carries a tag; each result declares the tags it needs. We pick the result
 * whose tags are all present, preferring the most specific (most tags matched)
 * so a catch-all can sit alongside precise outcomes without swallowing them.
 * The branching therefore lives in the theme editor, where the merchant can
 * change it, rather than in this file, where they cannot.
 *
 * The quiz also stops early. After each answer it works out which results are
 * still reachable — a result is reachable while every tag it needs has either
 * been chosen already or is still on offer in a question to come. When one
 * result is left there is nothing further worth asking, so we show it. That is
 * why picking the drops ends the quiz immediately instead of marching the
 * visitor through two more questions whose answers cannot change anything.
 *
 * Every result card is rendered by Liquid up front and hidden. Revealing one is
 * then a class change, with real prices, real availability and a working add to
 * cart — no client-side product fetch, and it degrades to a plain list of
 * products if JavaScript never arrives.
 */
(function () {
  'use strict';

  var quizzes = document.querySelectorAll('[data-sg-quiz]');
  if (!quizzes.length) return;

  Array.prototype.forEach.call(quizzes, function (quiz) { init(quiz); });

  function tagList(value) {
    return (value || '')
      .split(',')
      .map(function (t) { return t.trim(); })
      .filter(Boolean);
  }

  function init(quiz) {
    var steps = [].slice.call(quiz.querySelectorAll('[data-sg-quiz-step]'));
    var results = [].slice.call(quiz.querySelectorAll('[data-sg-quiz-result]'));
    var progress = quiz.querySelector('[data-sg-quiz-progress]');
    var bar = quiz.querySelector('[data-sg-quiz-bar]');
    var restart = quiz.querySelector('[data-sg-quiz-restart]');
    var back = quiz.querySelector('[data-sg-quiz-back]');
    var live = quiz.querySelector('[data-sg-quiz-live]');

    if (!steps.length || !results.length) return;

    var answers = [];
    var current = 0;

    function announce(text) {
      if (live && text) live.textContent = text;
    }

    function show(index) {
      current = index;
      steps.forEach(function (step, i) { step.hidden = i !== index; });
      results.forEach(function (r) { r.hidden = true; });
      quiz.classList.remove('sg-quiz--answered');

      if (restart) restart.hidden = true;
      if (back) back.hidden = index === 0;
      // No "of N": the quiz can finish early, and promising three questions
      // then delivering one reads as a bug rather than a kindness.
      if (progress) progress.textContent = 'Question ' + (index + 1);
      if (bar) bar.style.width = Math.round((index / steps.length) * 100) + '%';
    }

    // Tags still on offer in questions the visitor has not reached yet.
    function pendingTags() {
      var out = [];
      for (var i = current + 1; i < steps.length; i++) {
        var options = steps[i].querySelectorAll('[data-sg-quiz-option]');
        Array.prototype.forEach.call(options, function (option) {
          out.push(option.getAttribute('data-tag'));
        });
      }
      return out;
    }

    function reachable() {
      var pending = pendingTags();
      return results.filter(function (result) {
        return tagList(result.getAttribute('data-tags')).every(function (tag) {
          return answers.indexOf(tag) !== -1 || pending.indexOf(tag) !== -1;
        });
      });
    }

    function score(result) {
      var needed = tagList(result.getAttribute('data-tags'));
      // A result with no tags is a catch-all: it matches, but with the lowest
      // possible specificity so anything real outranks it.
      for (var i = 0; i < needed.length; i++) {
        if (answers.indexOf(needed[i]) === -1) return -1;
      }
      return needed.length;
    }

    function finish() {
      var best = null;
      var bestScore = -1;

      results.forEach(function (result) {
        var s = score(result);
        if (s > bestScore) { bestScore = s; best = result; }
      });

      if (!best) best = results[0];

      steps.forEach(function (step) { step.hidden = true; });
      results.forEach(function (r) { r.hidden = r !== best; });
      quiz.classList.add('sg-quiz--answered');

      if (restart) restart.hidden = false;
      if (back) back.hidden = false;
      if (progress) progress.textContent = 'Your match';
      if (bar) bar.style.width = '100%';

      announce('Recommended: ' + (best.getAttribute('data-name') || 'a match'));

      // Only pull the page if the card would otherwise open off-screen.
      var rect = best.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        best.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    quiz.addEventListener('click', function (event) {
      var option = event.target.closest('[data-sg-quiz-option]');
      if (option && quiz.contains(option)) {
        answers[current] = option.getAttribute('data-tag') || '';
        answers.length = current + 1;   // answering again invalidates later answers

        if (current + 1 >= steps.length || reachable().length <= 1) {
          finish();
        } else {
          show(current + 1);
          announce(steps[current].getAttribute('data-question'));
        }
        return;
      }

      if (event.target.closest('[data-sg-quiz-back]')) {
        if (quiz.classList.contains('sg-quiz--answered')) {
          // Back from a result returns to the question that produced it, which
          // is the last one actually answered — not necessarily the last step.
          show(Math.max(0, answers.length - 1));
        } else if (current > 0) {
          show(current - 1);
        }
        answers.length = current;
        return;
      }

      if (event.target.closest('[data-sg-quiz-restart]')) {
        answers = [];
        show(0);
        quiz.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }

      var add = event.target.closest('[data-sg-quiz-add]');
      if (add) {
        event.preventDefault();
        addToCart(add);
      }
    });

    show(0);
  }

  function addToCart(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId) return;

    button.disabled = true;

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1 }],
        sections: 'cart-drawer,cart-icon-bubble',
        sections_url: window.location.pathname
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (state) {
        if (state.status) throw new Error(state.description || state.message);

        // Hand the rendered sections to Dawn so the drawer opens with the item
        // already in it, exactly as it does from a product page.
        var drawer = document.querySelector('cart-drawer');
        if (drawer && typeof drawer.renderContents === 'function') {
          drawer.renderContents(state);
        } else {
          window.location = window.Shopify.routes.root + 'cart';
        }
      })
      .catch(function () { /* leave the visitor where they are */ })
      .then(function () { button.disabled = false; });
  }
})();